import { useEffect, useState } from "react";
import { useDailySettlementContext } from "../../hooks/daily-settlement-context";
import {
  DailySettlementRevenueContentHeader,
  DailySettlementRevenueMainBoxWrapper,
  DailySettlementRevenueMainContentWrapper,
} from "./style";
import { Title } from "../../../../styles/page";
import { Doughnut } from "react-chartjs-2";
import { useDailyRevenueChart } from "./hooks/use-daily-revenue-chart";
import {
  deleteRevenue,
  PaymentMethod,
  saveRevenue,
  SettlementRevenueDto,
  updateRevenue,
} from "../../../../api/settlement";
import Pagination from "../../../../component/Pagination";
import { formatDateWithDote } from "../../../../utils/date";
import { getPayTypeText } from "../../../solution/utils/settlement.util";
import DailySettlementEditPopup, {
  DailySettlementEditDto,
} from "../edit-popup";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../../recoil/pub.atom";
import {
  DailySettlementSectionWrapper,
  DailySettlementContentWrapper,
  DailySettlementRevenueMainTableBody,
  DailySettlementRevenueMainTableHeader,
  DailySettlementRevenueMainTableItem,
  DailySettlementRevenueMainTableWrapper,
  DailySettlementRecordTableItem,
  DailySettlementRecordTableWrapper,
  DailySettlementMainBox,
  DailySettlementSortArrowWrapper,
} from "../../style";
import {
  getMethodData,
  getMethodRate,
  revenueHeaders,
} from "../../utils/revenue";
import DailySettlementRecordTable from "../record-table";

export const methodDropBoxList = [
  { name: "현금", value: PaymentMethod.CASH },
  { name: "계좌이체", value: PaymentMethod.BANK_TRANSFER },
  { name: "카드", value: PaymentMethod.CARD },
];

const DailySettlementRevenue = () => {
  const {
    getDailyRevenue,
    date,
    dailyRevenue,
    isEditPopup,
    setIsEditPopup,
    revenue,
    setRevenue,
  } = useDailySettlementContext();
  const { chartData, options, centerTextPlugin } =
    useDailyRevenueChart(dailyRevenue);
  const pub = useRecoilValue(pubState);
  const [otherList, setOtherList] = useState<SettlementRevenueDto[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.CASH,
  );
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState(0);
  const [dropBoxOpen, setDropBoxOpen] = useState(false);
  const [sort, setSort] = useState("");
  const [order, setOrder] = useState<"DESC" | "ASC">("DESC");

  useEffect(() => {
    if (!revenue) return;
    setDescription(revenue.description);
    setAmount(revenue.amount);
    setPaymentMethod(revenue.paymentMethod);
  }, [revenue]);

  const editList: DailySettlementEditDto[] = [
    {
      name: "결제수단",
      type: "dropbox",
      value: paymentMethod,
      setValue: setPaymentMethod,
      dropBoxList: methodDropBoxList,
      open: dropBoxOpen,
      setOpen: setDropBoxOpen,
    },
    {
      name: "내역",
      type: "input",
      value: description,
      setValue: setDescription,
    },
    { name: "금액", type: "number", value: amount, setValue: setAmount },
  ];

  useEffect(() => {
    getDailyRevenue();
  }, [date]);

  // 페이지네이션 및 정렬
  useEffect(() => {
    if (!dailyRevenue) return;

    const direction = order === "ASC" ? 1 : -1;

    let list = [...dailyRevenue.revenues];

    // 리스트 정렬
    if (sort === "time") {
      list.sort(
        (a, b) =>
          (new Date(a?.updatedAt ?? "").getTime() -
            new Date(b?.updatedAt ?? "").getTime()) *
          direction,
      );
    } else if (sort === "amount") {
      list.sort((a, b) => (a.amount - b.amount) * direction);
    }

    // 페이지네이션
    setTotalPages(Math.max(1, Math.ceil(list.length / 11)));
    setOtherList(list.slice((currentPage - 1) * 11, currentPage * 11));
  }, [dailyRevenue, currentPage, sort, order]);

  const revenueBoxes = [
    {
      name: "게임 매출",
      info: "현금 + 계좌 + 카드",
      value: dailyRevenue?.gameRevenue,
    },
    {
      name: "전체 매출",
      info: "게임 매출 + 포인트",
      value: dailyRevenue?.allRevenue,
    },
    {
      name: "총 매출",
      info: "전체 매출 + 기타 매출 + 래빗",
      value: dailyRevenue?.totalRevenue,
      isTotal: true,
    },
  ];

  // 생성/수정 데이터 리셋
  const handleResetPopup = () => {
    setDescription("");
    setAmount(0);
    setPaymentMethod(PaymentMethod.CASH);
    setRevenue(null);
  };

  // 기타 매출 내역 생성
  const handleCreate = async () => {
    if (!pub) return;
    const request = {
      pubId: pub.id,
      description,
      amount,
      settledAt: date,
      paymentMethod,
    };
    try {
      await saveRevenue(request, pub.id, date);
      alert("생성 되었습니다.");
      getDailyRevenue();
      setIsEditPopup(false);
      handleResetPopup();
    } catch (e: any) {
      alert(e.message);
    }
  };

  // 기타 매출 내역 수정
  const handleUpdate = async () => {
    if (!pub || !revenue?.id) return;
    const request = {
      pubId: pub.id,
      description,
      amount,
      settledAt: date,
      paymentMethod,
    };
    try {
      await updateRevenue(request, revenue.id);
      alert("수정 되었습니다.");
      getDailyRevenue();
      setIsEditPopup(false);
      handleResetPopup();
    } catch (e: any) {
      alert(e.message);
    }
  };

  // 기타 매출 내역 삭제
  const handleDelete = async (id: number) => {
    const confirm = window.confirm("해당 매출 내역을 삭제하시겠습니까? ");
    if (!confirm) return;

    try {
      await deleteRevenue(id);
      alert("삭제 되었습니다.");
      getDailyRevenue();
      handleResetPopup();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleSort = (sortKey: string) => {
    if (sort === sortKey) {
      // 같은 컬럼 클릭 시 정렬 방향 토글
      setOrder(order === "DESC" ? "ASC" : "DESC");
    } else {
      // 다른 컬럼 클릭 시 해당 컬럼으로 정렬하고 DESC(높은순)로 시작
      setSort(sortKey);
      setOrder("DESC");
    }
  };

  const headers = [
    {
      header: "수정일시",
      className: "time",
      render: (item: SettlementRevenueDto) =>
        formatDateWithDote(item?.updatedAt || "", true),
      arrow: "time",
    },
    {
      header: "결제수단",
      className: "type",
      render: (item: SettlementRevenueDto) =>
        getPayTypeText(item.paymentMethod) ?? "",
    },
    {
      header: "내역",
      className: "memo",
      render: (item: SettlementRevenueDto) => item.description,
    },
    {
      header: "금액",
      className: "amount",
      render: (item: SettlementRevenueDto) => item.amount.toLocaleString(),
      arrow: "amount",
    },
    {
      header: "",
      className: "button",
      render: (item: SettlementRevenueDto) => "수정",
      onClick: (item: SettlementRevenueDto) => {
        setRevenue(item);
        setIsEditPopup(true);
      },
    },
    {
      header: "",
      className: "button red",
      render: (item: SettlementRevenueDto) => "삭제",
      onClick: (item: SettlementRevenueDto) => {
        item.id && handleDelete(item.id);
      },
    },
  ];

  return (
    <DailySettlementSectionWrapper>
      {isEditPopup && (
        <DailySettlementEditPopup
          title={
            revenue
              ? `${formatDateWithDote(date)} 기타 매출 수정`
              : `${formatDateWithDote(date)} 기타 매출 추가`
          }
          list={editList}
          onClose={() => {
            setIsEditPopup(false);
            handleResetPopup();
          }}
          onConfirm={revenue ? handleUpdate : handleCreate}
          data={revenue}
        />
      )}
      <DailySettlementContentWrapper width="976px">
        <Title weight={700}>금일 매출 개요</Title>
        <DailySettlementRevenueMainContentWrapper>
          <div className="chart">
            <Doughnut
              data={chartData}
              options={options}
              plugins={[centerTextPlugin]}
            />
          </div>
          <DailySettlementRevenueMainBoxWrapper>
            {revenueBoxes.map((box, index) => (
              <DailySettlementMainBox
                key={index}
                isPurple={box?.isTotal}
                width={box?.isTotal ? "372px" : ""}
                height="143px"
              >
                <div className="name">{box.name}</div>
                <div className="info">{box.info}</div>
                <div className="value">{box.value?.toLocaleString()}원</div>
              </DailySettlementMainBox>
            ))}
          </DailySettlementRevenueMainBoxWrapper>
        </DailySettlementRevenueMainContentWrapper>
        <DailySettlementRevenueMainTableWrapper isPadding={true}>
          <DailySettlementRevenueMainTableHeader>
            <div className="header" />
            {revenueHeaders.map((header, index) => (
              <div className="item" key={index}>
                {header.header}
              </div>
            ))}
          </DailySettlementRevenueMainTableHeader>
          <DailySettlementRevenueMainTableBody>
            <DailySettlementRevenueMainTableItem>
              <div className="header">게임 매출</div>
              <div className="header">기타 매출</div>
              <div className="header">비율</div>
            </DailySettlementRevenueMainTableItem>
            {revenueHeaders.map((header, index) => (
              <DailySettlementRevenueMainTableItem key={index}>
                <div className="item">
                  {getMethodData(
                    header.method,
                    dailyRevenue,
                  )?.tournamentRevenue?.toLocaleString()}
                </div>
                <div className="item">
                  {getMethodData(
                    header.method,
                    dailyRevenue,
                  )?.otherRevenue?.toLocaleString()}
                </div>
                <div className="item">
                  {getMethodRate(header.method, dailyRevenue)}%
                </div>
              </DailySettlementRevenueMainTableItem>
            ))}
          </DailySettlementRevenueMainTableBody>
        </DailySettlementRevenueMainTableWrapper>
      </DailySettlementContentWrapper>
      <DailySettlementContentWrapper width="522px">
        <DailySettlementRevenueContentHeader>
          <Title weight={700}>금일 기타 매출 내역</Title>
          <div className="box" onClick={() => setIsEditPopup(true)}>
            추가
          </div>
        </DailySettlementRevenueContentHeader>
        {otherList.length ? (
          <DailySettlementRecordTable
            headers={headers}
            sort={sort}
            order={order}
            list={otherList}
            handleSort={handleSort}
          />
        ) : (
          <div className="no-data">
            금일 기타 매출 내역이
            <br /> 없습니다.
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <Pagination
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        )}
      </DailySettlementContentWrapper>
    </DailySettlementSectionWrapper>
  );
};

export default DailySettlementRevenue;
